var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { typeAlert } from "@affinitysystemsgroup/github-packages-affinity-components/dist";
import { showErrorToast } from "../../../fnx/showError";
import { clearCustomRequest, customRequestApis, } from "../../../store/customRequest/actions";
import { getCollectionStatusLabel, } from "../../../model/collection";
import { COBRANZA_PAGE_SIZE, COBRANZA_SUMMARY_VISIBLE_STATUSES, } from "../constants/cobranza.constants";
import { useGetCollectionSummary } from "./useGetCollectionSummary";
var LIST_REQUEST_KEY = "cobranzaCollectionsList";
var POLL_INTERVAL_MS = 10000;
var POLL_MAX_DURATION_MS = 2 * 60 * 1000;
var mapListItem = function (item) {
    var _a, _b, _c, _d, _e;
    return ({
        id: item.id,
        statusRaw: item.status,
        statusLabel: getCollectionStatusLabel(item.status),
        billingType: (_b = (_a = item.parameters) === null || _a === void 0 ? void 0 : _a.billingType) !== null && _b !== void 0 ? _b : "",
        userName: (_d = (_c = item.parameters) === null || _c === void 0 ? void 0 : _c.userName) !== null && _d !== void 0 ? _d : "",
        createdAt: item.createdAt,
        batchFiles: (_e = item.batchFiles) !== null && _e !== void 0 ? _e : [],
        completionSummary: item.completionSummary,
    });
};
var mapDetail = function (item) {
    var _a;
    return ({
        id: item.id,
        statusRaw: item.status,
        statusLabel: getCollectionStatusLabel(item.status),
        createdAt: item.createdAt,
        parameters: item.parameters,
        batchFiles: (_a = item.batchFiles) !== null && _a !== void 0 ? _a : [],
        completionSummary: item.completionSummary,
    });
};
export var useCobranza = function () {
    var dispatch = useDispatch();
    var navigate = useNavigate();
    var t = useTranslation().t;
    var _a = useState("ALL"), statusFilter = _a[0], setStatusFilter = _a[1];
    var _b = useState(1), page = _b[0], setPage = _b[1];
    var _c = useState(null), selectedId = _c[0], setSelectedId = _c[1];
    var _d = useState({
        show: false,
        message: "",
        type: typeAlert.success,
    }), toast = _d[0], setToast = _d[1];
    var rawCollections = useSelector(function (state) { var _a, _b, _c; return (_c = (_b = (_a = state.CustomRequest) === null || _a === void 0 ? void 0 : _a.dataCustomRequest) === null || _b === void 0 ? void 0 : _b[LIST_REQUEST_KEY]) !== null && _c !== void 0 ? _c : null; });
    var loadingCollections = useSelector(function (state) { var _a, _b, _c; return (_c = (_b = (_a = state.CustomRequest) === null || _a === void 0 ? void 0 : _a.loadingCustomRequest) === null || _b === void 0 ? void 0 : _b[LIST_REQUEST_KEY]) !== null && _c !== void 0 ? _c : false; });
    var errorCollections = useSelector(function (state) { var _a, _b, _c; return (_c = (_b = (_a = state.CustomRequest) === null || _a === void 0 ? void 0 : _a.errorCustomRequest) === null || _b === void 0 ? void 0 : _b[LIST_REQUEST_KEY]) !== null && _c !== void 0 ? _c : null; });
    var totalItems = useSelector(function (state) { var _a, _b, _c, _d; return (_d = (_c = (_b = (_a = state.CustomRequest) === null || _a === void 0 ? void 0 : _a.pagingInfo) === null || _b === void 0 ? void 0 : _b[LIST_REQUEST_KEY]) === null || _c === void 0 ? void 0 : _c.totalItems) !== null && _d !== void 0 ? _d : 0; });
    var fetchCollections = function () {
        dispatch(customRequestApis({
            requestKey: LIST_REQUEST_KEY,
            method: "GET",
            path: "/asg/Api/V1/Collections",
            queryParams: __assign({ enable: true, page: page, limit: COBRANZA_PAGE_SIZE, orderDir: "desc", orderBy: "createdAt" }, (statusFilter !== "ALL" ? { status: statusFilter } : {})),
        }));
    };
    useEffect(function () {
        fetchCollections();
    }, [page, statusFilter]);
    var selectStatusFilter = function (value) {
        setStatusFilter(value);
        setPage(1);
    };
    useEffect(function () {
        return function () {
            dispatch(clearCustomRequest(LIST_REQUEST_KEY));
        };
    }, []);
    var pollTimeoutRef = useRef(null);
    var pollStartedAtRef = useRef(null);
    var clearPoll = function () {
        if (pollTimeoutRef.current) {
            clearTimeout(pollTimeoutRef.current);
            pollTimeoutRef.current = null;
        }
    };
    useEffect(function () {
        pollStartedAtRef.current = null;
        clearPoll();
    }, [page, statusFilter]);
    useEffect(function () { return clearPoll; }, []);
    useEffect(function () {
        if (errorCollections) {
            setToast({
                show: true,
                type: typeAlert.error,
                message: "Hubo inconvenientes al procesar la solicitud, por favor contacte con la aseguradora",
            });
        }
    }, [errorCollections]);
    var collections = useMemo(function () { return (rawCollections !== null && rawCollections !== void 0 ? rawCollections : []).map(mapListItem); }, [rawCollections]);
    useEffect(function () {
        clearPoll();
        var hasInProgress = collections.some(function (item) { return item.statusRaw === "GENERATING_BANK_FILE"; });
        if (!hasInProgress) {
            pollStartedAtRef.current = null;
            return;
        }
        if (pollStartedAtRef.current === null) {
            pollStartedAtRef.current = Date.now();
        }
        var elapsed = Date.now() - pollStartedAtRef.current;
        if (elapsed >= POLL_MAX_DURATION_MS) {
            return;
        }
        pollTimeoutRef.current = setTimeout(fetchCollections, POLL_INTERVAL_MS);
        return clearPoll;
    }, [collections]);
    useEffect(function () {
        if (collections.length === 0)
            return;
        var stillVisible = collections.some(function (item) { return item.id === selectedId; });
        if (!stillVisible) {
            setSelectedId(collections[0].id);
        }
    }, [collections]);
    var detail = useMemo(function () {
        var rawItem = (rawCollections !== null && rawCollections !== void 0 ? rawCollections : []).find(function (item) { return item.id === selectedId; });
        return rawItem ? mapDetail(rawItem) : null;
    }, [rawCollections, selectedId]);
    var summaryCollectionId = detail && COBRANZA_SUMMARY_VISIBLE_STATUSES.includes(detail.statusRaw)
        ? detail.id
        : null;
    var _e = useGetCollectionSummary(summaryCollectionId), summary = _e.summary, summaryError = _e.error;
    useEffect(function () {
        if (summaryError)
            showErrorToast(summaryError, setToast, t);
    }, [summaryError]);
    var selectCollection = function (id) {
        setSelectedId(id);
    };
    var applyPayments = function () {
        var _a, _b;
        if (!detail)
            return;
        navigate("/admin/collect", {
            state: {
                id: detail.id,
                usuario: (_b = (_a = detail.parameters) === null || _a === void 0 ? void 0 : _a.userName) !== null && _b !== void 0 ? _b : "",
                fecha: detail.createdAt,
                estado: detail.statusLabel,
                archivos: detail.batchFiles.map(function (file) { return file.url; }),
                referencia: "",
            },
        });
    };
    var goToGenerate = function () {
        navigate("/admin/billing");
    };
    var closeToast = function () {
        setToast({ show: false, message: "", type: typeAlert.success });
    };
    return {
        collections: collections,
        visibleCollections: collections,
        loadingCollections: loadingCollections,
        errorCollections: errorCollections,
        statusFilter: statusFilter,
        selectStatusFilter: selectStatusFilter,
        page: page,
        limit: COBRANZA_PAGE_SIZE,
        totalItems: totalItems,
        goToPage: setPage,
        selectedId: selectedId,
        selectCollection: selectCollection,
        detail: detail,
        summary: summary,
        applyPayments: applyPayments,
        goToGenerate: goToGenerate,
        toast: toast,
        closeToast: closeToast,
        refresh: fetchCollections,
    };
};
